import React, { FC, useMemo } from 'react';
import {
  useTranslate,
  TextInput,
  SelectInput,
  required,
  NumberInput,
  minValue,
  maxValue,
  SelectArrayInput,
  ArrayInput,
  SimpleFormIterator,
  FormDataConsumer,
} from 'react-admin';
import { useForm } from 'react-final-form';
import Button from '@material-ui/core/Button';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import { l } from '~/utils/solutions';
import { useStyles } from '~/hooks/useSolutionsStyles';
import { ProductTemplate, Solution } from '~/types';
import { AutocompleteArrayTextInput } from '~/components/ra';
import { useHandbooksQuery } from '~/hooks/useHandbook';
import { useProductsBySolutionId } from '~/hooks/product';
import { usePnlAccounts } from '~/hooks/pnlAccounts';
import { Configuration } from '~/api/solutions';
import { H3 } from '~/components';
import { useProductTemplates } from '~/hooks/useProductTemplates';

interface InvitationTransferFormProps {
  keyName: string;
  basePath: string;
  record: Solution;
  configurationRecord: Configuration;
}

export const InvitationTransferForm: FC<InvitationTransferFormProps> = ({
  keyName,
  record,
  configurationRecord,
}) => {
  const classes = useStyles();
  const translate = useTranslate();
  const t = (key: string, args?: any): string => translate(l(`featuresConfig.${key}`), args);
  const form = useForm();

  const validateHasEntries = (value: any[]) =>
    !value || value.length === 0 ? t('atLeastOneCurrency') : undefined;

  const { data: handbook, isLoading: handbookLoading } = useHandbooksQuery(record?.id);
  const kycLevels = handbookLoading
    ? []
    : handbook?.kycLevels?.map((kyc) => ({
        id: kyc?.id,
        name: kyc?.name,
      }));
  const currencyChoices = (handbook?.currencies ?? []).map((c) => ({ id: c.id, name: c.id }));

  const { data: productsTemplate } = useProductTemplates();
  const productsTemplateMap: Record<string, ProductTemplate> | undefined = useMemo(
    () => productsTemplate?.reduce((prev, cur) => Object.assign(prev, { [cur.id]: cur }), {}),
    [productsTemplate]
  );

  const { data: allProducts, isLoading: productsLoading } = useProductsBySolutionId(record?.id);
  const applicableProducts = productsLoading
    ? []
    : allProducts?.filter(
        (product) => productsTemplateMap?.[product?.templateCode]?.category !== 'CNL_ACCOUNT'
      );

  const { data: tenantAccounts } = usePnlAccounts();

  const validateNoDuplicate = (value: string, allValues: any) => {
    const rows = allValues?.[keyName]?.currencyMappingRules ?? [];
    return rows.filter((r: any) => r?.currency === value).length > 1
      ? t('duplicateCurrency')
      : undefined;
  };

  return (
    <>
      <NumberInput
        label={t('validityDuration')}
        source={`${keyName}.invitationExpirationDurationInSeconds`}
        min={MIN_VALIDITY_DURATION}
        max={MAX_VALIDITY_DURATION}
        step={1}
        className={classes.inputWidth}
        validate={[
          minValue(
            MIN_VALIDITY_DURATION_IN_SEC,
            t('validityDurationMinError', { min: MIN_VALIDITY_DURATION })
          ),
          maxValue(
            MAX_VALIDITY_DURATION_IN_SEC,
            t('validityDurationMaxError', { max: MAX_VALIDITY_DURATION })
          ),
          required(),
        ]}
        format={(value?: number) => (value ? Math.floor(value / 86400) : '')}
        parse={(value?: number) => (value ? value * 86400 : undefined)}
      />
      <SelectArrayInput
        choices={kycLevels}
        source={`${keyName}.targetKycLevel`}
        label={t('targetKYC')}
        className={classes.inputWidth}
        alwaysOn={false}
        translateChoice={false}
        allowEmpty
        format={(v: any) => (Array.isArray(v) ? v : [])}
        parse={(value: any) => (value && value.length > 0 ? value : [])}
      />
      <AutocompleteArrayTextInput
        label={t('phonePrefixes')}
        source={`${keyName}.phonePrefixes`}
        freeSolo
        regularHelperText={t('phoneHelperText')}
        className={classes.inputWidth}
        record={configurationRecord}
      />
      <TextInput
        label={t('termsAndConditionsLink')}
        source={`${keyName}.termsAndConditionsLink`}
        className={classes.inputWidth}
        validate={required()}
      />
      <TextInput
        label={t('websiteLink')}
        source={`${keyName}.websiteLink`}
        className={classes.inputWidth}
        validate={required()}
      />
      <H3>{t('currencyMappingRules')}</H3>
      <ArrayInput
        source={`${keyName}.currencyMappingRules`}
        label=''
        className={classes.simpleArrayInput}
        validate={validateHasEntries}
      >
        <SimpleFormIterator
          addButton={
            <Button startIcon={<AddCircleOutlineIcon />} color='default'>
              {t('addCurrencyMappingRule')}
            </Button>
          }
        >
          <FormDataConsumer>
            {({ getSource, scopedFormData }: any) => (
              <>
                <SelectInput
                  source={getSource('currency')}
                  label={t('currency')}
                  choices={currencyChoices}
                  validate={[required(), validateNoDuplicate]}
                  className={classes.inputWidth}
                  onChange={() => {
                    form.change(getSource('transitAccount'), undefined);
                    form.change(getSource('targetProducts'), []);
                  }}
                />
                <SelectInput
                  source={getSource('transitAccount')}
                  label={t('escrowAccount')}
                  choices={
                    tenantAccounts
                      ?.filter((a) => a.currency === scopedFormData?.currency)
                      .map((a) => ({ id: a.id, name: a.alias })) ?? []
                  }
                  disabled={!scopedFormData?.currency}
                  validate={required()}
                  className={classes.inputWidth}
                  translateChoice={false}
                />
                <SelectArrayInput
                  source={getSource('targetProducts')}
                  label={t('targetProducts')}
                  choices={
                    applicableProducts
                      ?.filter((p) => p.currency === scopedFormData?.currency)
                      .map((p) => ({ id: p.id, name: p.name })) ?? []
                  }
                  disabled={!scopedFormData?.currency}
                  validate={required()}
                  className={classes.inputWidth}
                  translateChoice={false}
                  format={(v: any) => (Array.isArray(v) ? v : [])}
                />
              </>
            )}
          </FormDataConsumer>
        </SimpleFormIterator>
      </ArrayInput>
    </>
  );
};

const MIN_VALIDITY_DURATION = 1;
const MAX_VALIDITY_DURATION = 90;

const MIN_VALIDITY_DURATION_IN_SEC = MIN_VALIDITY_DURATION * 86400;
const MAX_VALIDITY_DURATION_IN_SEC = MAX_VALIDITY_DURATION * 86400;
